import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
type HTMLElementOrNull = HTMLElement | null;
type CallbackRef<T extends HTMLElement | null = HTMLElementOrNull> = (node: T) => void;
const config: ResizeObserverOptions = {
  box: 'content-box'
};

/**
 *
 * useResizeObserverRef hook
 *
 * Returns a resize observer for a React Ref and fires a callback
 * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * @param {ResizeObserverCallback} callback Function that needs to be fired on resize
 * @param {ResizeObserverOptions} options An options object allowing you to set options for the observation
 * @returns {[CallbackRef]} callbackref
 * @see https://rooks.vercel.app/docs/useResizeObserverRef
 */
function useResizeObserverRef(callback, t0) {
  const $ = _c(9);
  const options = t0 === undefined ? config : t0;
  const {
    box
  } = options;
  const [node, setNode] = useState(null);
  const callbackRef = useRef(callback);
  let t1;
  if ($[0] !== callback) {
    t1 = () => {
      callbackRef.current = callback;
    };
    $[0] = callback;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (...t3) => {
      const args = t3;
      callbackRef.current?.(...args);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleResizeObserver = t2;
  let t3;
  let t4;
  if ($[3] !== box || $[4] !== node) {
    t3 = () => {
      if (node) {
        const observer = new ResizeObserver(handleResizeObserver);
        observer.observe(node, {
          box
        });
        return () => {
          observer.disconnect();
        };
      }
      return _temp;
    };
    t4 = [node, handleResizeObserver, box];
    $[3] = box;
    $[4] = node;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = element => {
      setNode(element);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const ref = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [ref];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function _temp() {}
export { useResizeObserverRef };